import React, { useMemo } from 'react';
import {
  buildMonthGrid, buildWeekGrid, groupByDateKey, toDateKey,
  WEEKDAY_LABELS_HU, WEEKDAY_SHORT_HU, MEDICAL_EVENT_LABELS, useAnimalCoverUrls,
} from '@rehome/shared';
import type { AnimalMedicalReminder, CalendarDay } from '@rehome/shared';
import { AlertTriangle, Loader2 } from 'lucide-react';

export type CalendarMode = 'month' | 'week';

interface ReminderCalendarProps {
  mode: CalendarMode;
  viewDate: Date;
  reminders: AnimalMedicalReminder[];
  loading: boolean;
  truncated: boolean;
  onSelect: (reminder: AnimalMedicalReminder) => void;
}

const todayKey = () => toDateKey(new Date());

const isOverdue = (r: AnimalMedicalReminder) => Boolean(r.nextDueDate && r.nextDueDate < todayKey());

/** Az emlékeztetők állat-portréihoz — a privát képek aláírt linkje kötegelve. */
const useReminderCovers = (reminders: AnimalMedicalReminder[]) => {
  const animals = useMemo(
    () => reminders.map(r => r.animal).filter(Boolean) as any[],
    [reminders]
  );
  return useAnimalCoverUrls(animals);
};

/**
 * Emlékeztetők naptárnézete.
 *
 * A hónap- és hétnézet ugyanabból az adatból dolgozik, de MÁS sűrűséggel:
 * egy hónapcellába legfeljebb néhány apró portré fér, egy hétoszlopba viszont
 * elfér a megnevezés, a típus és az állat neve is. A rács hétfővel kezdődik,
 * a mai nap kiemelt.
 */
export const ReminderCalendar: React.FC<ReminderCalendarProps> = ({
  mode, viewDate, reminders, loading, truncated, onSelect,
}) => {
  const byDate = useMemo(
    () => groupByDateKey(reminders, r => r.nextDueDate),
    [reminders]
  );
  const covers = useReminderCovers(reminders);

  const weeks = useMemo(() => buildMonthGrid(viewDate), [viewDate]);
  const week = useMemo(() => buildWeekGrid(viewDate), [viewDate]);

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 bg-white/60 z-10 flex items-start justify-center pt-10">
          <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
        </div>
      )}

      {truncated && (
        <div className="mb-3 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Ebben az időszakban túl sok az emlékeztető ahhoz, hogy mind elférjen.
            Szűkíts állatra vagy típusra a teljes képhez.
          </span>
        </div>
      )}

      {mode === 'month'
        ? <MonthGrid weeks={weeks} byDate={byDate} covers={covers} onSelect={onSelect} />
        : <WeekGrid days={week} byDate={byDate} covers={covers} onSelect={onSelect} />}
    </div>
  );
};

// ─── Hónap nézet ────────────────────────────────────────────────────────────

const MAX_AVATARS = 4;

const MonthGrid: React.FC<{
  weeks: CalendarDay[][];
  byDate: Record<string, AnimalMedicalReminder[]>;
  covers: Record<string, string>;
  onSelect: (r: AnimalMedicalReminder) => void;
}> = ({ weeks, byDate, covers, onSelect }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden">
    <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
      {WEEKDAY_LABELS_HU.map((label, i) => (
        <div key={label} className={`px-2 py-2 text-xs font-semibold text-center ${i >= 5 ? 'text-gray-400' : 'text-gray-600'}`}>
          <span className="hidden sm:inline">{label}</span>
          <span className="sm:hidden">{WEEKDAY_SHORT_HU[i]}</span>
        </div>
      ))}
    </div>

    <div className="divide-y divide-gray-200">
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 divide-x divide-gray-200">
          {week.map(day => {
            const items = byDate[day.key] || [];
            return (
              <div
                key={day.key}
                className={`min-h-[92px] p-1.5 ${
                  !day.inCurrentMonth ? 'bg-gray-50/60' : day.isWeekend ? 'bg-gray-50/30' : 'bg-white'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`inline-flex items-center justify-center text-xs font-semibold rounded-full w-6 h-6 ${
                      day.isToday
                        ? 'bg-teal-600 text-white'
                        : day.inCurrentMonth ? 'text-gray-700' : 'text-gray-300'
                    }`}
                  >
                    {day.date.getDate()}
                  </span>
                  {items.length > MAX_AVATARS && (
                    <span className="text-[10px] font-medium text-gray-400">{items.length}</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1">
                  {items.slice(0, MAX_AVATARS).map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => onSelect(r)}
                      title={`${r.animal?.name ?? ''} — ${MEDICAL_EVENT_LABELS[r.eventType]}: ${r.title}`}
                      aria-label={`${r.animal?.name ?? 'Állat'} — ${r.title}`}
                      className={`relative w-7 h-7 rounded-full overflow-hidden border-2 transition-transform hover:scale-110 focus:scale-110 focus:outline-none focus:ring-2 focus:ring-teal-400 ${
                        isOverdue(r) ? 'border-red-400' : 'border-white ring-1 ring-gray-200'
                      }`}
                    >
                      {r.animal && covers[r.animal.id] ? (
                        <img src={covers[r.animal.id]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center bg-teal-100 text-teal-800 text-[10px] font-bold">
                          {(r.animal?.name || '?').charAt(0).toUpperCase()}
                        </span>
                      )}
                    </button>
                  ))}
                  {items.length > MAX_AVATARS && (
                    <button
                      type="button"
                      onClick={() => onSelect(items[MAX_AVATARS])}
                      className="w-7 h-7 rounded-full bg-gray-100 border border-gray-200 text-[10px] font-bold text-gray-500 hover:bg-gray-200 transition-colors"
                      aria-label={`További ${items.length - MAX_AVATARS} emlékeztető`}
                    >
                      +{items.length - MAX_AVATARS}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  </div>
);

// ─── Hét nézet ──────────────────────────────────────────────────────────────

const WeekGrid: React.FC<{
  days: CalendarDay[];
  byDate: Record<string, AnimalMedicalReminder[]>;
  covers: Record<string, string>;
  onSelect: (r: AnimalMedicalReminder) => void;
}> = ({ days, byDate, covers, onSelect }) => (
  // Hét oszlop egy sorban: több hely jut egy napra, mint a hónapnézetben,
  // ezért itt a megnevezés és a típus is elfér a portré mellett.
  <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
    {days.map((day, i) => {
      const items = byDate[day.key] || [];
      return (
        <div
          key={day.key}
          className={`rounded-xl border overflow-hidden flex flex-col ${
            day.isToday ? 'border-teal-400 ring-2 ring-teal-100' : 'border-gray-200'
          }`}
        >
          <div className={`px-3 py-2 border-b ${day.isToday ? 'bg-teal-50 border-teal-200' : day.isWeekend ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
            <div className={`text-[11px] font-semibold ${day.isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>
              {WEEKDAY_LABELS_HU[i]}
            </div>
            <div className={`text-lg font-bold ${day.isToday ? 'text-teal-700' : 'text-gray-800'}`}>
              {day.date.getDate()}
              {day.isToday && <span className="ml-1 text-[10px] font-semibold uppercase tracking-wide">ma</span>}
            </div>
          </div>

          <div className="p-2 space-y-1.5 flex-1 min-h-[80px]">
            {items.length === 0 && (
              <p className="text-[11px] text-gray-300 text-center pt-3">—</p>
            )}
            {items.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => onSelect(r)}
                className={`w-full text-left rounded-lg border px-2 py-1.5 transition-colors hover:border-teal-300 hover:bg-teal-50/40 ${
                  isOverdue(r) ? 'border-red-200 bg-red-50/50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 ring-1 ring-gray-200">
                    {r.animal && covers[r.animal.id] ? (
                      <img src={covers[r.animal.id]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="w-full h-full flex items-center justify-center bg-teal-100 text-teal-800 text-[9px] font-bold">
                        {(r.animal?.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </span>
                  <span className="text-xs font-bold text-gray-900 truncate">{r.animal?.name ?? '—'}</span>
                  {isOverdue(r) && <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />}
                </div>
                <div className="text-[11px] text-gray-600 truncate">{r.title}</div>
                <div className="text-[10px] text-gray-400 truncate">{MEDICAL_EVENT_LABELS[r.eventType]}</div>
              </button>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);
